<template>
  <q-page :class="{ 'shared-page-padding': isSharedView }">
    <img v-if="isSharedView" src="/static/img/relevize-logo-navy_transparent.png" style="height: 60px; margin-top: 30px; margin-bottom: 30px;">

    <div class="page-header flex justify-between">
      <h1 class="rel-page-header q-mb-lg q-mt-none">
        Ad Metrics
      </h1>
    </div>
    <div v-if="userStore.user.role === 'vendor' || isSharedView" class="radio-filters col-12 q-mt-none q-mb-md">
      <q-radio
        v-model="reportType"
        :disable="tab === 'chart'"
        val="all"
        label="Group Campaigns by Partner"
        data-test="group-campaigns-by-partner"
      />
      <q-radio
        v-model="reportType"
        :disable="tab === 'chart'"
        val="partner"
        label="Group by Partner"
        data-test="group-by-partner"
      />
      <q-radio
        v-model="reportType"
        :disable="tab === 'chart'"
        val="campaign"
        label="Group by Campaign"
        data-test="group-by-campaign"
      />
    </div>
    <div class="controls col-12 q-my-md row items-center">
      <div class="filters flex row col-12 col-md-7 col-lg-6 col-xl-6">
        <r-date-picker
          class="col-12 col-md-5 q-mr-sm"
          :date-range="dateRange"
          :min-date="new Date(2022, 6, 1)"
          :allow-past-dates="true"
          data-test="date-picker"
          @update:date-range="updateDateRange"
        />
        <filter-select
          v-if="reportType !== 'campaign' && (userStore.user.role === 'vendor' || isSharedView)"
          class="col-12 col-md-1 q-pl-none q-mr-sm"
          :list="partnerCategories || []"
          :selected="selectedPartners"
          :is-loading="fetchingReportsBeta"
          label="Partners"
          data-test="partners-dropdown"
          @update-selected="updateSelectedPartners"
        />
        <filter-select
          v-if="reportType !== 'partner'"
          class="col-12 col-md-1 q-pl-none q-mr-sm"
          :list="partnerCampaignCategories || []"
          :selected="selectedPartnerCampaigns"
          :is-loading="fetchingReportsBeta"
          label="Campaigns"
          data-test="campaigns-dropdown"
          @update-selected="updateSelectedPartnerCampaigns"
        />
        <filter-select
          v-if="reportType === 'all'"
          class="col-12 col-md-1 q-pl-none q-mr-sm"
          :list="campaignStatusOptions"
          :selected="selectedCampaignStatus"
          :isLoading="false"
          label="Status"
          data-test="campaign-status-dropdown"
          @update-selected="updateCampaignStatus"
        />
        <div v-if="dateRange?.length || selectedPartners.length || selectedPartnerCampaigns.length" class="clear-all flex row col-6 col-md-2 col-lg-auto">
          <q-btn
            class="link-small clear-all"
            flat
            label="Clear All"
            @click="removeFilters"
          />
        </div>
      </div>
      <div class="tabs flex row justify-end col-12 col-lg-3">
        <div v-if="tab === 'chart'" class="chart-options col-auto flex justify-start ga-1">
          <RelDropdownSelectVuetify
            v-model="selectedChartType"
            class="chart-type-options col-5 col-lg-1"
            data-test="chart-type-dropdown"
            :items="chartTypeOptions"
            :clearable="false"
            :filterable="false"
            :searchable="false"
          />
          <q-select
            v-model="selectedMetric"
            class="col-5 col-lg-1"
            data-test="metric-dropdown"
            outlined
            dense
            input-debounce="0"
            :options="metricOptions"
            @update:model-value="assignMetricData"
          />
        </div>
        <div v-if="!isSharedView" class="row flex justify-end">
          <q-tabs
            v-model="tab"
            inline-label
            dense
            active-class="active"
          >
            <q-tab
              class="table-cta"
              name="table"
              :icon="symOutlinedTable"
              no-caps
            >
              <span style="font-size: 16px;line-height: 1.715em;font-weight: 500;padding-left:8px;">Table</span>
            </q-tab>
            <q-tab
              class="chart-cta"
              name="chart"
              label="Chart"
              icon="show_chart"
              no-caps
            />
          </q-tabs>
        </div>
        <RelButtonIcon
          v-if="!isSharedView"
          name="download"
          icon-type="symbol"
          type="secondary"
          class="ms-2"
          @click="initiateCSVDownload"
        >
          <RelTooltip text="Download CSV" />
        </RelButtonIcon>
      </div>
    </div>
    <div v-if="showAdMetricsLoadingIndicator" class="row justify-center items-center">
      <q-spinner color="primary" size="3em" />
    </div>
    <q-banner v-else-if="reportTotals" class="ad-metrics-banner col-12 q-my-md">
      <div class="stats-banner flex row justify-between text-center">
        <div class="col-6 col-sm-auto q-mb-md">
          <p class="banner-titles">
            Total Leads
          </p>
          <h2 class="q-my-xs banner-stats">
            {{ formatComma(reportTotals.leads) || 0 }}
          </h2>
        </div>
        <div class="col-6 col-sm-auto q-mb-md">
          <p class="banner-titles">
            Avg CPL
          </p>
          <h2 class="q-my-xs banner-stats">
            ${{ formatComma(roundToDecimalPlaces(reportTotals.cpl, 2)) || 0 }}
          </h2>
        </div>
        <div class="col-6 col-sm-auto q-mb-md">
          <p class="banner-titles">
            Total Impressions
          </p>
          <h2 class="q-my-xs banner-stats">
            {{ formatComma(reportTotals.impressions) || 0 }}
          </h2>
        </div>
        <div class="col-6 col-sm-auto q-mb-md">
          <p class="banner-titles">
            Total Clicks
          </p>
          <h2 class="q-my-xs banner-stats">
            {{ formatComma(reportTotals.clicks) || 0 }}
          </h2>
        </div>
        <div class="col-12 col-sm-auto">
          <p class="banner-titles">
            Avg CTR
          </p>
          <h2 class="q-my-xs banner-stats">
            {{ ctrToPercentage }}%
          </h2>
        </div>
      </div>
    </q-banner>
    <div v-if="showAdMetricsLoadingIndicator" class="row justify-center items-center" style="background-color: white; min-height: 298px;">
      <q-spinner color="primary" size="3em" />
    </div>
    <q-tab-panels v-else v-model="tab" :class="{'q-mt-lg': tab === 'table'}" class="q-tab-panels__overflow-visible">
      <q-tab-panel style="padding: 0;" name="table">
        <PrimarySurface class="row">
          <div class="col-12">
            <q-table
              :pagination="{ 
                rowsPerPage: 100,
                sortBy: 'campaign_status',
                descending: false
              }"
              :rows="tableRows"
              :rows-per-page-options="[]"
              :columns="tableColumns"
              flat
            >
              <template #top-row>
                <q-tr class="total-row" style="background-color: #f5f7fb;">
                  <q-td :colspan="colSpanForTopRow">
                    Totals
                  </q-td>
                  <q-td v-for="column in tableColumnsForTotals" :key="column.name">
                    {{ formatDisplayForTableCell({ value: calculatedTotals[column.name], columnName: column.name }) }}
                  </q-td>
                </q-tr>
              </template>
              <template #body="props">
                <q-tr :props="props" class="ad-row">
                  <q-td v-if="reportType === 'all' || reportType === 'partner'" key="partner_name" :style="{ minWidth: userStore.user.role === 'vendor' ? '340px' : '240px' }">
                    <RelAvatarCompany
                      v-if="userStore.user.role === 'vendor' || isSharedView"
                      :image="`https://relevize.com/secure-image/placement_profile_pictures/${props.row.partner_logo}`"
                      :text="props.row.partner_name || ''"
                      style="margin-left: 5px;"
                    />
                    <span class="ms-2" />
                    <template v-if="isSharedView">
                      {{ props.row.partner_name }}
                    </template>
                    <template v-else>
                      <template v-if="userStore.user.role === 'vendor'">
                        {{ props.row.partner_name }}
                      </template>
                      <template v-else>
                        {{ props.row.vendor_name }}
                      </template>
                    </template>
                    <q-btn
                      v-if="!isSharedView"
                      class="small-button primary edit-user-button"
                      icon-right="open_in_new"
                      label="Ads"
                      no-caps
                      size="sm"
                      style="float: right; font-size: 13px; width:90px; color: white;"
                      @click="openInNewWindow(props.row.linkedin_ad_account_url)"
                    >
                      <q-tooltip anchor="top middle" self="bottom middle">
                        View on LinkedIn
                      </q-tooltip>
                    </q-btn>
                  </q-td>

                  <q-td v-if="reportType === 'all'" key="first_spend_date" style="min-width: 150px">
                    {{ formatDate(props.row.first_spend_date) }}
                  </q-td>

                  <q-td v-if="reportType === 'all' || reportType === 'campaign'" key="campaign_name" style="min-width: 375px; display: flex; align-items: center;">
                    <div v-if="props.row.campaign_background_image_url" class="campaign-image-container" style="position: relative; display: inline-block;" @click="openImagePreview(props.row.campaign_background_image_url)">
                      <img :src="props.row.campaign_background_image_url" style="width: 45px; margin-right: 8px; cursor: pointer;">
                      <q-icon name="search" class="magnifier-icon" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0; transition: opacity 0.2s;" />
                    </div>
                    <div v-else>
                      <img :src="defaultImage" style="width: 45px; margin-right: 8px;">
                    </div>
                    <span style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">{{ props.row.campaign_name }}</span>

                    <!-- Only show the button if we're filtering by campaigns: -->
                    <q-btn
                      v-if="reportType === 'campaign'"
                      class="small-button primary edit-user-button"
                      icon-right="open_in_new"
                      label="Ads"
                      no-caps
                      size="sm"
                      style="float: right; font-size: 13px; width:90px; color: white;"
                      @click="openInNewWindow(props.row.linkedin_ad_account_url)"
                    >
                      <q-tooltip>
                        View on LinkedIn
                      </q-tooltip>
                    </q-btn>
                  </q-td>
                  <q-td v-if="reportType === 'all'" key="campaign_status" style="min-width: 110px">
                    <RelChip v-if="props.row.campaign_status" :label="stringToTitleCase(props.row.campaign_status)" v-bind="getChipColorProps(stringToTitleCase(props.row.campaign_status))" data-test="status" />
                  </q-td>
                  <q-td v-for="column in tableColumnsForTotals" :key="column.name" :props="props">
                    {{ typeof(props.row[column.name]) === 'number' ? formatDisplayForTableCell({ value: props.row[column.name], columnName: column.name }) : props.row[column.name] }}
                  </q-td>
                </q-tr>
              </template>
            </q-table>
          </div>
        </PrimarySurface>
      </q-tab-panel>
      <q-tab-panel name="chart">
        <div v-if="selectedChartType === 'Line Chart'">
          <ad-metrics-line-chart />
        </div>
        <div v-if="selectedChartType === 'Bar Chart'">
          <ad-metrics-bar-chart />
        </div>
      </q-tab-panel>
    </q-tab-panels>
    <q-dialog v-model="showImageModal">
      <q-card>
        <q-img :src="previewImageUrl" style="width: 500px;" />
        <q-card-actions align="right">
          <q-btn flat label="Close" @click="showImageModal = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script setup>
import { computed, onMounted, ref, toRefs, watch } from 'vue'
import { QTd, useQuasar } from 'quasar'
import { symOutlinedTable } from '@quasar/extras/material-symbols-outlined'
import dayjs from 'dayjs'
import { useRoute, useRouter } from 'vue-router'

import RDatePicker from '@components/RDatePicker.vue'
import FilterSelect from '@components/ui/FilterSelect.vue'
import { stringToTitleCase } from '@components/campaigns/campaign-helpers'
import { RelChip } from '@/components-rel'

import { useUserStore } from '@stores/users'
import { useReportsStore } from '@stores/reports'
import { useGeneralStore } from '@stores/general'
import {RelAvatarCompany} from '@/components-rel'

import AdMetricsLineChart from '@components/reports/AdMetricsLineChart.vue'
import AdMetricsBarChart from '@components/reports/AdMetricsBarChart.vue'

import { formatComma, roundToDecimalPlaces } from '@utils/helpers'
import { RelButtonIcon, RelTooltip } from '@/components-rel'
import PrimarySurface from '@components/ui/PrimarySurface.vue'
import defaultImage from '/campaign/defaultImage.svg'

const allTableColumns = [
  { name: 'partner_name', field: 'partner_name', label: 'Partner Name', align: 'left', sortable: true },
  { name: 'first_spend_date', field: 'first_spend_date', label: 'Start Date', align: 'left', sortable: true },
  { name: 'campaign_name', field: 'campaign_name', label: 'Campaign Name', align: 'left', sortable: true },
  { name: 'campaign_status', field:'campaign_status', label: 'Status', align: 'left', sortable: true },
  { name: 'impressions', field:'impressions', label: 'Impressions', align: 'left', sortable: true },
  { name: 'spend', field:'spend', label: 'Spend', align: 'left', sortable: true },
  { name: 'total_budget', field: 'total_budget', label: 'Total Budget', align: 'left', sortable: true },
  { name: 'budget_spent_percentage', field:'budget_spent_percentage', label: '% Budget Spent', align: 'left', sortable: true },
  { name: 'budget_remaining', field:'budget_remaining', label: 'Budget Remaining ($)', align: 'left', sortable: true },
  { name: 'cpm', field:'cpm', label: 'CPM', align: 'left', sortable: true },
  { name: 'clicks', field:'clicks', label: 'Clicks', align: 'left', sortable: true },
  { name: 'engagements', field:'engagements', label: 'Engagements', align: 'left', sortable: true },
  { name: 'ctr', field:'ctr', label: 'CTR', align: 'left', sortable: true },
  { name: 'cpc', field:'cpc', label: 'CPC', align: 'left', sortable: true },
  { name: 'cpe', field:'cpe', label: 'CPE', align: 'left', sortable: true },
  { name: 'leads', field:'leads', label: 'Leads', align: 'left', sortable: true },
  { name: 'cpl', field:'cpl', label: 'CPL', align: 'left', sortable: true },
  { name: 'lead_form_opens', field:'lead_form_opens', label: 'Form Opens', align: 'left', sortable: true },
  { name: 'lead_form_open_rate', field:'lead_form_open_rate', label: 'Form Completion Rate', align: 'left', sortable: true },
]

const userStore = useUserStore()
const reportsStore = useReportsStore()
const generalStore = useGeneralStore()
const route = useRoute()
const router = useRouter()

const quasar = useQuasar()

const { selectedTags } = toRefs(generalStore)
const { partnerCategories, partnerCampaignCategories, campaignReport, partnerReport, allReport, reportTotals, showAdMetricsLoadingIndicator, selectedMetric } = toRefs(reportsStore)

const fetchingReportsBeta = ref(false)

const dateRange = ref()
const selectedPartners = ref([])
const selectedPartnerCampaigns = ref([])
const selectedCampaignStatus = ref([])

const reportType = ref('all')
const tab = ref('table')
const filterSelection = ref([])
const searchFilter = ref('')
const columnFormatTypes = {
  dollar: ['spend', 'cpm', 'cpc', 'cpe', 'cpl', 'budget_remaining', 'total_budget'],
  percent: ['ctr'],
}
const selectedChartType = ref('Line Chart')
const chartTypeOptions = [
  'Line Chart',
  'Bar Chart'
]

const metricOptions = [
  { label: 'Total Leads', value: 'leads'} ,
  { label: 'Total Impressions', value: 'impressions' },
  { label: 'Total Clicks', value: 'clicks' },
  { label: 'Total Engagements', value: 'engagements' },
  { label: 'Avg CPM', value: 'cpm' },
  { label: 'Avg CTR', value: 'ctr' },
  { label: 'Avg CPC', value: 'cpc' },
  { label: 'Avg CPE', value: 'cpe' },
  { label: 'Avg CPL', value: 'cpl' },
  { label: 'Total Spend', value: 'spend' },
]

const campaignStatusOptions = computed(() => {
  let rows;
  if (reportType.value === 'campaign' || userStore.user.role === 'partner') {
    rows = campaignReport.value
  } else if (reportType.value === 'partner') {
    rows = partnerReport.value
  } else {
    rows = allReport.value
  }
  const unique = [...new Set(rows.map(row => row.campaign_status))].filter(status => status != null)
  return unique.map(status => ({ label: status, value: status }))
})

const chipColors = {
  'Draft': 'gray-500',
  'Approved': 'light-green',
  'Active': 'dark-green',
  'Paused': 'yellow',
  'Completed': 'gray-500',
  'Archived': 'gray-300',
  'legacy': 'blue',
  'Asset': 'blue',
  'Meeting': 'blue',
  'Event': 'blue'
}

const chipColorProps = {
  'blue': {
    color: 'alice-blue',
    textColor: 'sapphire',
  },
  'light-green': {
    color: 'mint-cream',
    textColor: 'india-green',
  },
  'dark-green': {
    color: 'india-green',
    textColor: 'white',
  },
  'gray-300': {
    color: 'ghost-white',
    textColor: 'gray300',
  },
  'gray-500': {
    color: 'ghost-white',
    textColor: 'gray500',
  },
  'yellow': {
    color: 'papaya-whip',
    textColor: 'gray500',
  }
}

const getChipColorProps = label => {
  return chipColorProps[chipColors[label]]
}

// Add computed property to check if we're in shared view
const isSharedView = computed(() => route.meta.isSharedView)

watch(tab, () => {
  updateFilters()
  if (tab.value === 'chart') {
    reportType.value = null
  } else {
    reportType.value = 'all'
  }
})

// Function to update URL with current filters
const updateUrlParams = () => {
  const query = {}
  
  if (reportType.value !== 'all') {
    query.type = reportType.value
  }
  
  if (dateRange.value?.length === 2) {
    query.from = dayjs(dateRange.value[0]).format('YYYY-MM-DD')
    query.to = dayjs(dateRange.value[1]).format('YYYY-MM-DD')
  }
  
  if (selectedPartners.value?.length) {
    query.partners = selectedPartners.value.join(',')
  }
  
  if (selectedPartnerCampaigns.value?.length) {
    query.campaigns = selectedPartnerCampaigns.value.join(',')
  }
  
  if (selectedCampaignStatus.value?.length) {
    query.status = selectedCampaignStatus.value.join(',')
  }
  
  router.replace({ query })
}

// Function to read URL params and set filters
const initializeFromUrl = () => {
  const { type, from, to, partners, campaigns, status } = route.query
  
  if (type && ['all', 'partner', 'campaign'].includes(type)) {
    reportType.value = type
  }
  
  if (from && to) {
    dateRange.value = [dayjs(from).toDate(), dayjs(to).toDate()]
  }
  
  if (partners) {
    selectedPartners.value = partners.split(',')
  }
  
  if (campaigns) {
    selectedPartnerCampaigns.value = campaigns.split(',')
  }
  
  if (status) {
    selectedCampaignStatus.value = status.split(',')
  }
}

// Watch for filter changes and update URL
watch([reportType, dateRange, selectedPartners, selectedPartnerCampaigns, selectedCampaignStatus], () => {
  updateUrlParams()
}, { deep: true })

onMounted(async() => {
  if (!fetchingReportsBeta.value) {
    fetchingReportsBeta.value = true
    // Initialize filters from URL before fetching data
    initializeFromUrl()
    await reportsStore.fetchAdMetricsReports({
      isSharedView: isSharedView.value,
      dateRange: dateRange.value,
      partnerIds: selectedPartners.value,
      campaignIds: selectedPartnerCampaigns.value
    })
    fetchingReportsBeta.value = false
  }
})

watch(() => selectedTags, () => {
  updateFilters()
}, {deep:true})

const ctrToPercentage = computed(() => {
  const value = reportTotals.value.ctr
  return roundToDecimalPlaces(value, 2)
})

const tableRows = computed(() => {
  let rows
      
  if (reportType.value === 'campaign' || userStore.user.role === 'partner') {
    rows = campaignReport.value
  } else if (reportType.value === 'partner') {
    rows = partnerReport.value
  } else {
    rows = allReport.value
  }

  if (searchFilter.value) {
    rows = rows.filter((row) => {
      return row.campaign_name?.toLowerCase().includes(searchFilter.value?.toLowerCase()) || row.partner_name?.toLowerCase().includes(searchFilter.value?.toLowerCase())
    })
  }
  if (selectedCampaignStatus.value?.length) {
    rows = rows.filter(row => selectedCampaignStatus.value.includes(row.campaign_status))
  }
  if (filterSelection.value?.length) {
    rows = filterSelection.value
  }

  if (reportType.value === 'partner' && (userStore.user.role === 'vendor' || isSharedView.value)) {
    rows = rows.map(row => {
      const spend = Math.round(row.spend * 100) / 100; // Round to 2 decimal places
      const total_budget = Math.round(row.total_budget * 100) / 100; // Round to 2 decimal places
      const budget_remaining = Math.max(0, Math.round((total_budget - spend) * 100) / 100); // Ensure non-negative and round
      return {
        ...row,
        spend,
        total_budget,
        budget_spent_percentage: total_budget ? spend / total_budget : 0,
        budget_remaining
      }
    })
  }

  return rows
})

const tableColumns = computed(() => {
  let columns = allTableColumns.map(column => {
    if (column.name === 'first_spend_date') {
      return {
        ...column,
        sortMethod: (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
      }
    }
    return column
  })

  // Check if dateRange is set and filter out the specific columns
  if (dateRange.value && (dateRange.value[0] || dateRange.value[1])) {
    columns = columns.filter(column => 
      column.name !== 'lead_form_opens' && 
      column.name !== 'lead_form_open_rate' &&
      column.name !== 'total_budget' &&
      column.name !== 'budget_spent_percentage' &&
      column.name !== 'budget_remaining'
    );
  }

  // Filter out first_spend_date column if report type is not 'all'
  if (reportType.value !== 'all') {
    columns = columns.filter(column => column.name !== 'first_spend_date' && column.name !== 'campaign_status');
  }

  // Handle partner type specific columns
  if (reportType.value === 'partner') {
    columns = columns.filter(column => 
      column.name !== 'campaign_name' && 
      // Only include budget columns if there's no date filter
      (!(dateRange.value && (dateRange.value[0] || dateRange.value[1])) || 
        !['total_budget', 'budget_spent_percentage', 'budget_remaining'].includes(column.name))
    );
  }
  // Handle campaign type specific columns
  else if (reportType.value === 'campaign') {
    columns = columns.filter(column => {
      return column.name !== 'partner_name' &&
             column.name !== 'total_budget' &&
             column.name !== 'budget_spent_percentage' &&
             column.name !== 'budget_remaining';
    });
  }
  // For all type, keep all relevant columns but ensure proper order
  else if (reportType.value === 'all') {
    columns = columns.filter(column => {
      return column.name !== 'total_budget' &&
             column.name !== 'budget_spent_percentage' &&
             column.name !== 'budget_remaining';
    });
  }

  return columns;
})

const tableColumnsForTotals = computed(() => {
  const excludedColumns = ['partner_name', 'campaign_name', 'first_spend_date', 'campaign_status']
  if (dateRange.value && (dateRange.value[0] || dateRange.value[1])) {
    excludedColumns.push('lead_form_opens', 'lead_form_open_rate', 'total_budget', 'budget_spent_percentage', 'budget_remaining')
  }

  if (reportType.value !== 'partner' || (dateRange.value && (dateRange.value[0] || dateRange.value[1]))) {
    excludedColumns.push('total_budget', 'budget_spent_percentage', 'budget_remaining')
  }

  return allTableColumns.filter((column) => !excludedColumns.includes(column.name))
})

function formatDisplayForTableCell({ value, columnName }) {
  // Correctly formats the numbers in table cells with commas and rounts to two decimals
  // for floats. Also adds dollar or percent signs for columns that require those.
  let formattedValue = value

  if (value?.toString().includes('.')) {
    formattedValue = parseFloat(formattedValue).toFixed(2)
  }
  if (columnName !== 'spend' && columnName !== 'total_budget') {
    formattedValue = formattedValue?.toLocaleString('en-US')
  }

  if (columnName === 'spend' || columnName === 'budget_remaining' || columnName === 'total_budget') {
    formattedValue = Math.round(parseFloat(value)) // Round to nearest whole number
    formattedValue = formattedValue.toLocaleString('en-US')
  }

  if (columnFormatTypes?.dollar.includes(columnName)) {
    formattedValue = `$${formattedValue}`
  } else if (columnFormatTypes?.percent.includes(columnName)) {
    formattedValue = `${formattedValue}%`
  } else if (columnName === 'lead_form_open_rate') {
    formattedValue = `${Math.round(formattedValue)}%`
  } else if (columnName === 'budget_spent_percentage') {
    formattedValue = `${(value * 100).toFixed(0)}%`
  }
  return formattedValue
}

const calculatedTotals = computed(() => {
  if (!filterSelection.value?.length) {
    // if they haven't filtered by anything, we can use the calculated totals from the back-end
    const total_budget = tableRows.value.reduce((sum, row) => sum + (row.total_budget || 0), 0)
    const budget_remaining = tableRows.value.reduce((sum, row) => sum + (row.budget_remaining || 0), 0)
    const budget_spent_percentage = ((total_budget - budget_remaining) / total_budget)

    return {
      ...reportTotals.value,
      total_budget,
      budget_remaining,
      budget_spent_percentage,
    }
  }
  const totals = {
    impressions: 0,
    spend: 0,
    clicks: 0,
    engagements: 0,
    leads: 0,
    cpm: 0,
    ctr: 0,
    cpc: 0,
    cpe: 0,
    cpl: 0,
    total_budget: 0,
    budget_remaining: 0,
  }
  filterSelection.value.forEach((row) => {
    totals.impressions += row.impressions
    totals.spend += row.spend
    totals.clicks += row.clicks
    totals.engagements += row.engagements
    totals.leads += row.leads
    totals.cpm = totals.cpm ? (totals.cpm + row.cpm) / 2 : row.cpm
    totals.ctr = totals.ctr ? (totals.ctr + row.ctr) / 2 : row.ctr
    totals.cpc = totals.cpc ? (totals.cpc + row.cpc) / 2 : row.cpc
    totals.cpe = totals.cpe ? (totals.cpe + row.cpe) / 2 : row.cpe
    totals.cpl = totals.cpl ? (totals.cpl + row.cpl) / 2 : row.cpl
    totals.total_budget += row.total_budget || 0
    totals.budget_remaining += row.budget_remaining
  })
  totals.budget_spent_percentage = 55
  return totals
})

const colSpanForTopRow = computed(() => {
  if (reportType.value === 'all') {
    return 4
  } else if (reportType.value === 'campaign') {
    return 1
  } else if (reportType.value === 'partner') {
    return 1
  }
  return 2 // default case
})

function initiateCSVDownload() {
  quasar.notify({
    message: 'CSV download in progress. It might take a few minutes. Please keep this browser window open.',
    color: 'info'
  })

  const payload = {}
  payload.report_type = 'admetrics'
  if (userStore.getUserRole === 'vendor') {
    payload.admetrics_filter = reportType.value
  }
  reportsStore.generateReportsCSV(payload, isSharedView.value)
}

function updateDateRange(range, callUpdateFilters = true) {
  if (dateRange.value === undefined || (dayjs(dateRange.value[0]).format('YYYY-MM-DD') !== dayjs(range[0]).format('YYYY-MM-DD') || dayjs(dateRange.value[1]).format('YYYY-MM-DD') !== dayjs(range[1]).format('YYYY-MM-DD'))) {
    dateRange.value = range
    if (callUpdateFilters) {
      updateFilters()
    }
  }
}

function updateSelectedPartners(value = [], callUpdateFilters = true) {
  if (JSON.stringify(selectedPartners.value) !== JSON.stringify(value)) {
    selectedPartners.value = JSON.parse(JSON.stringify(value))
    if (callUpdateFilters) {
      updateFilters()
    }
  }
}

function updateSelectedPartnerCampaigns(value = [], callUpdateFilters = true) {
  if (JSON.stringify(selectedPartnerCampaigns.value) !== JSON.stringify(value)) {
    selectedPartnerCampaigns.value = JSON.parse(JSON.stringify(value))
    if (callUpdateFilters) {
      updateFilters()
    }
  }
}

function updateCampaignStatus(value = [], callUpdateFilters = true) {
  if (JSON.stringify(selectedCampaignStatus.value) !== JSON.stringify(value)) {
    selectedCampaignStatus.value = JSON.parse(JSON.stringify(value))
    if (callUpdateFilters) {
      updateFilters()
    }
  }
}

function assignMetricData(metricName) {
  selectedMetric.value = metricName
  updateFilters()
}

// this is the main function to update filters
async function updateFilters() {
  if (!fetchingReportsBeta.value) {
    fetchingReportsBeta.value = true
    await reportsStore.fetchAdMetricsReports({ 
      isSharedView: isSharedView.value, 
      dateRange: dateRange.value, 
      partnerIds: selectedPartners.value, 
      campaignIds: selectedPartnerCampaigns.value 
    })
    fetchingReportsBeta.value = false
  }

  if (tab.value === 'chart') {
    reportsStore.fetchAdMetricsChartData({ 
      dateRange: dateRange.value, 
      partnerIds: selectedPartners.value, 
      campaignIds: selectedPartnerCampaigns.value, 
      adMetric: selectedMetric.value.value 
    })
  }
}

function removeFilters() {
  updateSelectedPartners([], false)
  updateSelectedPartnerCampaigns([], false)
  dateRange.value = undefined
  updateDateRange(undefined, false)
  selectedCampaignStatus.value = []
  updateFilters()
}

function openInNewWindow(url) {
  if (url) {
    window.open(url, '_blank')
  }
}

function formatDate(date) {
  return date ? dayjs(date).format('M/D/YY') : ''
}

// Added preview modal reactive variables and function
const showImageModal = ref(false)
const previewImageUrl = ref(null)
function openImagePreview(url) {
  previewImageUrl.value = url;
  showImageModal.value = true;
}

</script>

<style lang="scss" scoped>
.controls {
  min-height: 40px;

  .r-date-picker, .q-btn.filter-select-button, .q-tabs {
    height: 40px;
    margin-bottom: 1em;
    min-width: 150px;
  }
  .clear-all {
    height: 40px;
    color: rgb(var(--v-theme-relevize-black));
  }
  .chart-options {
    justify-self: end;
  }
}
.disabled {
  opacity: 0.5 !important;
}
:deep(.q-radio__inner) {
  width: 32px;
  height: 32px;
  color: rgb(var(--v-theme-relevize-black));
  font-size: 14px;
}
:deep(.q-btn.filter-select-button .label) {
  height: 33px;
}

:deep(.table-cta .q-icon > img) {
  width: 14px;
  height: 14px;
}
.active {
  background: rgb(var(--v-theme-primary))!important;
  border-color: rgb(var(--v-theme-primary))!important;
  color: #FFFFFF !important;

  :deep(.q-icon) {
    color: #FFFFFF !important;
  }
}
.ad-metrics-banner {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  gap: 40px;
  width: 100%;
  min-height: 99px;
  background: #f5f7fb;
  border-radius: 3px;
}

:deep(.ad-metrics-banner .q-banner__avatar) {
  display: none;
}

.banner-stats {
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
}

.banner-titles {
  margin-bottom: 4px;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
}

:deep(.q-tab) {
  min-width: 77px;
  height: 40px; 
  padding: 0 8px;
  border: 1px solid rgb(var(--v-theme-relevize-black));
  border-radius: 3px 0px 0px 3px;

  &:last-child {
    border-radius: 0px 3px 3px 0px;
  }
}
.chart-cta {
  border-radius: 0px 3px 3px 0px;
}
.chart-cta {
  border-radius: 0px 3px 3px 0px;
}

:deep(.q-tab--active .q-tab__indicator) {
  opacity: 0;
}

:deep(.q-select) {
  min-width: 150px;
}
.v-select {
  height: 40px;
}

:deep(.q-table th), .total-row {
  font-weight: 600;
  font-size: 12px;
  line-height: 100%;
}

:deep(.q-tab-panels__overflow-visible) {
  overflow: visible;
  .q-panel.scroll, .q-tab-panel {
    overflow: visible
  }
}

@media (max-width: $breakpoint-xs-max) {
  .radio-filters {
    display: flex;
    flex-direction: column;
  }
  .ad-metrics-banner-inner {
    display: flex;
    flex-direction: column;
    row-gap: 1em;
  }
}
@media (min-width: $breakpoint-md-min) {
  .controls {
    justify-content: space-between;
    .filters {
      width: 53%;
    }

    .tabs {
      width: 44%;
      justify-content: flex-end;
    }

    .chart-options, .q-tabs, .tabs {
      align-self: center;
      margin-top: 0 !important;
      margin-bottom: 0;
    }

    .r-date-picker, .q-btn.filter-select-button {
      margin-top: 0;
      margin-bottom: 0;
      margin-right: 5px;
    }
    .q-btn.filter-select-button {
      width: 150px;
    }
    .v-select {
      width: 150px;
      margin: 0;
    }
    label.q-field {
      margin-top: 0;
    }
  }
}
@media (max-width: $breakpoint-md-max) {
  .chart-options {
    margin-bottom: 1em;
  }
}
@media (max-width: $breakpoint-lg-max) {
  .controls {
    .chart-options {
      margin-right: 1em;
    }
    .tabs {
      margin-top: 8px;
      
      & > div {
        justify-content: flex-start;
      }
    }
  }
}
@media (min-width: $breakpoint-lg-min) {
  .controls {
    .v-select {
      width: 140px;
    }
    .tabs, .controls .chart-options {
      justify-content: flex-end;
    }
    .controls .chart-options {
      margin-right: 1em;
    }
  }

  :deep(.q-select) {
    min-width: 130px;
  }

  .stats-banner > div {
    margin-bottom: 0 !important;
  }
}

.q-table--no-wrap th, .q-table--no-wrap td {
  white-space: normal !important;
}

.edit-user-button{
  gap: 0px;
}

.ad-row .edit-user-button {
  display: none; /* Hide button by default */
}

.ad-row:hover .edit-user-button {
  display: flex; /* Show button on hover */
  position: absolute;
  right: 0;
  top: 50%; /* Align vertically in the middle */
  transform: translateY(-50%); /* Center align vertically */
  z-index: 1; /* Ensure it is on top of everything else in the cell */
}

.shared-page-padding {
  padding: 32px;
}

@media (max-width: $breakpoint-xs-max) {
  .shared-page-padding {
    padding: 0 16px;
  }
}

/* Added hover style for magnifier icon */
.campaign-image-container:hover .magnifier-icon {
  opacity: 1 !important;
}

.q-tr {
  background-color: #ffffff00; 
}
</style>
